import { useEffect, useMemo, useState } from "react";
import DateInput from "@/components/DateInput";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { BookOpen, ArrowsClockwise, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmt = (n) =>
  new Intl.NumberFormat("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    .format(Number(n || 0));

const tipoLabel = {
  activo: "Activo", pasivo: "Pasivo", patrimonio: "Patrimonio",
  ingreso: "Ingreso", egreso: "Egreso",
};

export default function SumasSaldosPage() {
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [soloMov, setSoloMov] = useState(true);
  const [data, setData] = useState({ filas: [], totales: { debe: 0, haber: 0, diferencia: 0 } });
  const [loading, setLoading] = useState(false);
  const [mayorCuenta, setMayorCuenta] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (desde) q.set("desde", desde);
      if (hasta) q.set("hasta", hasta);
      q.set("solo_con_movimientos", String(soloMov));
      const { data } = await api.get(`/contabilidad/sumas-y-saldos?${q}`);
      setData(data);
    } catch (e) {
      toast.error("No se pudo cargar Sumas y Saldos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [soloMov]);

  const cuadratura = useMemo(() => {
    const dif = data?.totales?.diferencia ?? 0;
    if (Math.abs(dif) < 0.01) return { ok: true, msg: "Balance cuadrado" };
    return { ok: false, msg: `Descuadre: $ ${fmt(dif)}` };
  }, [data]);

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading">Balance de Sumas y Saldos</h1>
          <p className="text-sm text-neutral-500">
            Normas contables argentinas. Debe = Haber si el balance está cuadrado.
          </p>
        </div>
        <Badge className={cuadratura.ok ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-100 text-red-700 border-red-300"}>
          {cuadratura.msg}
        </Badge>
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <Label>Desde</Label>
            <DateInput value={desde} onChange={(e) => setDesde(e.target.value)} data-testid="ss-desde" />
          </div>
          <div>
            <Label>Hasta</Label>
            <DateInput value={hasta} onChange={(e) => setHasta(e.target.value)} data-testid="ss-hasta" />
          </div>
          <div className="flex items-center gap-2">
            <Switch id="solomov" checked={soloMov} onCheckedChange={setSoloMov} data-testid="ss-solo-mov" />
            <Label htmlFor="solomov" className="cursor-pointer">Solo cuentas con movimientos</Label>
          </div>
          <div>
            <Button onClick={load} disabled={loading} className="w-full" data-testid="ss-refresh">
              <ArrowsClockwise size={16} className="mr-2" /> {loading ? "Cargando…" : "Actualizar"}
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-100 text-left">
              <tr>
                <th className="p-2 w-24">Código</th>
                <th className="p-2">Cuenta</th>
                <th className="p-2 w-24">Tipo</th>
                <th className="p-2 w-32 text-right">Debe</th>
                <th className="p-2 w-32 text-right">Haber</th>
                <th className="p-2 w-32 text-right">Saldo</th>
                <th className="p-2 w-16"></th>
              </tr>
            </thead>
            <tbody>
              {data.filas.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-neutral-400">
                    {loading ? "Cargando…" : "Sin movimientos contables en el período"}
                  </td>
                </tr>
              ) : data.filas.map((f) => (
                <tr key={f.codigo} className="border-t hover:bg-neutral-50" data-testid={`ss-row-${f.codigo}`}>
                  <td className="p-2 font-mono">{f.codigo}</td>
                  <td className="p-2">{f.nombre}</td>
                  <td className="p-2">
                    <Badge variant="outline" className="text-[10px]">{tipoLabel[f.tipo] || f.tipo}</Badge>
                  </td>
                  <td className="p-2 text-right font-mono">{fmt(f.debe)}</td>
                  <td className="p-2 text-right font-mono">{fmt(f.haber)}</td>
                  <td className="p-2 text-right font-mono">
                    {fmt(f.saldo)}
                    <span className={`ml-1 text-[10px] ${f.saldo_naturaleza === "deudor" ? "text-blue-700" : "text-red-700"}`}>
                      {f.saldo_naturaleza === "deudor" ? "D" : f.saldo_naturaleza === "acreedor" ? "A" : ""}
                    </span>
                  </td>
                  <td className="p-2">
                    <Button size="sm" variant="ghost" onClick={() => setMayorCuenta(f)} title="Ver mayor" data-testid={`ss-mayor-${f.codigo}`}>
                      <BookOpen size={16} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
            {data.filas.length > 0 && (
              <tfoot className="bg-neutral-100 font-bold">
                <tr className="border-t-2 border-black">
                  <td colSpan={3} className="p-2 text-right">TOTALES</td>
                  <td className="p-2 text-right font-mono">$ {fmt(data.totales.debe)}</td>
                  <td className="p-2 text-right font-mono">$ {fmt(data.totales.haber)}</td>
                  <td className="p-2 text-right font-mono">
                    <span className={cuadratura.ok ? "text-emerald-700" : "text-red-700"}>
                      $ {fmt(data.totales.diferencia)}
                    </span>
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>

      <MayorCuentaDialog
        cuenta={mayorCuenta}
        desde={desde}
        hasta={hasta}
        onClose={() => setMayorCuenta(null)}
      />
    </div>
  );
}

function MayorCuentaDialog({ cuenta, desde, hasta, onClose }) {
  const [mayor, setMayor] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!cuenta) { setMayor(null); return; }
    (async () => {
      setLoading(true);
      try {
        const q = new URLSearchParams();
        if (desde) q.set("desde", desde);
        if (hasta) q.set("hasta", hasta);
        const { data } = await api.get(`/contabilidad/mayor/${encodeURIComponent(cuenta.codigo)}?${q}`);
        setMayor(data);
      } finally { setLoading(false); }
    })();
  }, [cuenta, desde, hasta]);

  return (
    <Dialog open={!!cuenta} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="mayor-cuenta-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono">
            {cuenta?.codigo} · {cuenta?.nombre}
            <span className="ml-2 text-xs text-neutral-500 font-sans">Libro Mayor</span>
          </DialogTitle>
          <DialogDescription>
            {desde && `Desde ${desde} `}{hasta && `Hasta ${hasta}`}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="p-8 text-center text-neutral-400">Cargando…</div>
        ) : !mayor ? null : (
          <div className="space-y-3">
            {mayor.saldo_anterior_naturaleza !== "cero" && (
              <div className="text-sm text-neutral-600">
                Saldo anterior: <b>$ {fmt(mayor.saldo_anterior)}</b>{" "}
                <span className="text-xs">({mayor.saldo_anterior_naturaleza})</span>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-neutral-100 text-left">
                  <tr>
                    <th className="p-2 w-24">Fecha</th>
                    <th className="p-2 w-16">Nº As.</th>
                    <th className="p-2">Concepto</th>
                    <th className="p-2 w-28 text-right">Debe</th>
                    <th className="p-2 w-28 text-right">Haber</th>
                    <th className="p-2 w-28 text-right">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {mayor.movimientos.length === 0 ? (
                    <tr><td colSpan={6} className="p-4 text-center text-neutral-400">Sin movimientos</td></tr>
                  ) : mayor.movimientos.map((m, i) => (
                    <tr key={i} className="border-t">
                      <td className="p-2 font-mono">{m.fecha}</td>
                      <td className="p-2 font-mono">{m.numero}</td>
                      <td className="p-2">{m.concepto}</td>
                      <td className="p-2 text-right font-mono">{m.debe ? fmt(m.debe) : "-"}</td>
                      <td className="p-2 text-right font-mono">{m.haber ? fmt(m.haber) : "-"}</td>
                      <td className="p-2 text-right font-mono">
                        {fmt(m.saldo)}
                        <span className={`ml-1 text-[9px] ${m.saldo_naturaleza === "deudor" ? "text-blue-700" : "text-red-700"}`}>
                          {m.saldo_naturaleza === "deudor" ? "D" : m.saldo_naturaleza === "acreedor" ? "A" : ""}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-neutral-100 font-bold">
                  <tr className="border-t-2 border-black">
                    <td colSpan={3} className="p-2 text-right">TOTALES</td>
                    <td className="p-2 text-right font-mono">$ {fmt(mayor.totales.debe)}</td>
                    <td className="p-2 text-right font-mono">$ {fmt(mayor.totales.haber)}</td>
                    <td className="p-2 text-right font-mono">
                      $ {fmt(mayor.totales.saldo)}
                      <span className={`ml-1 text-[9px] ${mayor.totales.saldo_naturaleza === "deudor" ? "text-blue-700" : "text-red-700"}`}>
                        {mayor.totales.saldo_naturaleza === "deudor" ? "D" : mayor.totales.saldo_naturaleza === "acreedor" ? "A" : ""}
                      </span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
